import { Grid2, Card } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

import { CompanyDetailsResponse } from '@/models/payment';
import { SubTitle, Title } from '@/typography/Typography';
import { FormActionButtons } from '@/components/shared/FormActionButtons';
import { InputText } from '@/components/shared/InputText';
import { useNotificationsSnackbar } from '@/components/snackbar/NotificationsSnackbarContext';
import { GridContainer } from '@/pages/company/componants/Base';
import { usePaymentsMutations } from '@/services/paymentsMutations';
import { useErrorHandling } from '@/utils/errorHandling';
import { AutoTopUpSettings } from './AutoTopUpSettings';

export const LowBalanceSettings = ({
    currentCompany
}: {
    currentCompany?: CompanyDetailsResponse;
}) => {
    const { t } = useTranslation();
    const { setAlert } = useNotificationsSnackbar();
    const { updateCompanyDetails } = usePaymentsMutations();
    const { backendErrorHandler } = useErrorHandling();

    const [lowBalanceEmail, setLowBalanceEmail] = useState(
        currentCompany?.lowBalanceEmail || ''
    );
    const [lowBalanceThreshold, setLowBalanceThreshold] = useState(
        currentCompany?.lowBalanceNotificationThreshold || 0
    );
    const [lowBalanceEmailError, setLowBalanceEmailError] = useState(false);
    const [lowBalanceThresholdError, setLowBalanceThresholdError] =
        useState(false);

    // Auto Top-up state
    const isConnected = currentCompany?.autoTopupStatus === 'Connected';
    const [isAutoTopupEnabled, setIsAutoTopupEnabled] = useState(false);
    const [topUpAmount, setTopUpAmount] = useState(
        currentCompany?.autoTopupAmount || 0
    );

    // Track initial values for dirty state detection
    const [initialLowBalanceEmail, setInitialLowBalanceEmail] = useState('');
    const [initialLowBalanceThreshold, setInitialLowBalanceThreshold] =
        useState(0);
    const [initialTopUpAmount, setInitialTopUpAmount] = useState(0);

    // Compute if form is dirty
    const isDirty =
        lowBalanceEmail !== initialLowBalanceEmail ||
        lowBalanceThreshold != initialLowBalanceThreshold ||
        topUpAmount !== initialTopUpAmount;

    const initialValues = () => {
        const email = currentCompany?.lowBalanceEmail || '';
        const threshold = currentCompany?.lowBalanceNotificationThreshold || 0;
        const amount = currentCompany?.autoTopupAmount || 0;

        setLowBalanceEmail(email);
        setLowBalanceThreshold(threshold);
        setLowBalanceEmailError(false);
        setLowBalanceThresholdError(false);

        // Reset Auto Top-up state
        setIsAutoTopupEnabled(
            currentCompany?.autoTopupStatus === 'Connected' &&
                (currentCompany?.autoTopupAmount ?? 0) > 0
        );
        setTopUpAmount(amount);

        // Set initial values for dirty tracking
        setInitialLowBalanceEmail(email);
        setInitialLowBalanceThreshold(threshold);
        setInitialTopUpAmount(amount);
    };

    useEffect(() => {
        initialValues();
    }, [
        currentCompany?.lowBalanceEmail,
        currentCompany?.lowBalanceNotificationThreshold,
        currentCompany?.autoTopupStatus,
        currentCompany?.autoTopupAmount
    ]);

    useEffect(() => {
        if (updateCompanyDetails.isSuccess) {
            setAlert(t('settings_saved'), 'success');
        }
    }, [updateCompanyDetails.isSuccess]);

    useEffect(() => {
        if (updateCompanyDetails.isError) {
            backendErrorHandler()(updateCompanyDetails.error);
        }
    }, [updateCompanyDetails.isError]);

    const isValidLowBalanceData = () => {
        let isValid = true;
        const isAutoTopupSet =
            isConnected && isAutoTopupEnabled && topUpAmount > 0;

        if (!lowBalanceThreshold) {
            setLowBalanceThresholdError(true);
            isValid = false;
        } else {
            setLowBalanceThresholdError(false);
        }

        if (!lowBalanceEmail) {
            setLowBalanceEmailError(true);
            isValid = false;
        } else {
            setLowBalanceEmailError(false);
        }

        // Additional validation when auto top-up is enabled
        if (isAutoTopupSet) {
            if (!lowBalanceThreshold) {
                setLowBalanceThresholdError(true);
                isValid = false;
            }
            if (!lowBalanceEmail) {
                setLowBalanceEmailError(true);
                isValid = false;
            }
        }

        return isValid;
    };

    const handleSaveChanges = () => {
        if (isValidLowBalanceData()) {
            updateCompanyDetails.mutate({
                lowBalanceEmail,
                lowBalanceNotificationThreshold: Number(lowBalanceThreshold),
                autoTopupAmount: topUpAmount
            });
        }
    };

    const handleSaveAndConnect = async (): Promise<boolean> => {
        if (isValidLowBalanceData()) {
            try {
                await updateCompanyDetails.mutateAsync({
                    lowBalanceEmail,
                    lowBalanceNotificationThreshold:
                        Number(lowBalanceThreshold),
                    autoTopupAmount: topUpAmount
                });
                return true;
            } catch (error) {
                return false;
            }
        }
        return false;
    };

    const handleCancel = () => {
        initialValues();
    };

    return (
        <Grid2
            container
            mt={3}
            pt={5}
            justifyContent={'space-between'}
            borderTop={'1px solid #D0D5DD'}
        >
            <Grid2 size={{ xs: 3 }} display={'block'}>
                <Title>{t('low_balance')}</Title>
                <SubTitle marginBottom={2}>
                    {t('low_balance_description_1')}
                </SubTitle>
                <SubTitle marginBottom={2}>
                    {t('low_balance_description_2')}
                </SubTitle>
                {/* Add this hidden element to store the connection status */}
                <div
                    id="auto-topup-connection-status"
                    data-status={
                        currentCompany?.autoTopupStatus || 'NotConnected'
                    }
                    style={{ display: 'none' }}
                >
                    {currentCompany?.autoTopupStatus}
                </div>
            </Grid2>
            <Grid2 size={{ xs: 8.8 }}>
                <Card>
                    <GridContainer container margin={'0 !important'}>
                        <InputText
                            type="number"
                            value={lowBalanceThreshold}
                            onChange={(e) =>
                                setLowBalanceThreshold(e.target.value as any)
                            }
                            label={t('set_low_balance_amount')}
                            placeholder="Default is set to 10,000 DKK"
                            error={lowBalanceThresholdError}
                            helperText={
                                lowBalanceThresholdError &&
                                isConnected &&
                                isAutoTopupEnabled &&
                                topUpAmount > 0
                                    ? 'This field can not be empty when Auto top-up amount is set.'
                                    : ''
                            }
                        />
                        <SubTitle>
                            If the company balance drops below this number,
                            you&apos;ll receive an email reminder to deposit
                            funds.
                        </SubTitle>
                    </GridContainer>
                    <GridContainer container>
                        <InputText
                            value={lowBalanceEmail}
                            onChange={(e) => setLowBalanceEmail(e.target.value)}
                            label={t('low_balance_notification')}
                            placeholder="Enter one or more email addresses (separated by commas)"
                            error={lowBalanceEmailError}
                            helperText={
                                lowBalanceEmailError &&
                                isConnected &&
                                isAutoTopupEnabled &&
                                topUpAmount > 0
                                    ? 'This field can not be empty when Auto top-up amount is set.'
                                    : ''
                            }
                        />
                        <SubTitle>Separate each email with a comma,</SubTitle>
                    </GridContainer>

                    <GridContainer
                        // mt={3}
                        container
                        borderTop={'1px solid #D0D5DD'}
                    >
                        <AutoTopUpSettings
                            currentCompany={currentCompany}
                            isAutoTopupEnabled={isAutoTopupEnabled}
                            setIsAutoTopupEnabled={setIsAutoTopupEnabled}
                            topUpAmount={topUpAmount}
                            setTopUpAmount={setTopUpAmount}
                            onSaveAndConnect={handleSaveAndConnect}
                        />
                    </GridContainer>

                    <FormActionButtons
                        show={isDirty}
                        onSave={handleSaveChanges}
                        onCancel={handleCancel}
                        isLoading={updateCompanyDetails.isPending}
                    />
                </Card>
            </Grid2>
        </Grid2>
    );
};
